import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { getPartnerViewAnalyticsConfig } from "@/v2/apis/partnerView/partner-view-apis";
import { Loading } from "@/v2/ui/composed/loading";
import {
  buildTimeRangeNativeFilter,
  resolveDashboardTimeFilterId,
} from "@/v2/utils/supersetNativeFilters";

interface PartnerViewEmbedProps {
  supersetUrl: string;
  dashboardId: string;
  onEmbedError: () => void;
  /**
   * Optional override for the guest-token fetcher. Defaults to the public
   * partner-view analytics_config endpoint (used by the partner-facing route).
   * The admin "Preview as partner" flow injects an admin-side fetcher so the
   * embed reuses this component byte-for-byte while sourcing the token from
   * the Firebase-authenticated admin endpoint instead.
   */
  fetchGuestToken?: () => Promise<string>;
  /**
   * Optional [from, to] ISO date strings (``YYYY-MM-DD``) for the chrome's
   * date range picker. Plumbed through to the Superset embed via the SDK's
   * ``urlParams`` so the dashboard's native time filter picks them up on
   * first render. Changing either string remounts the iframe — Superset's
   * embed SDK only reads urlParams at boot, so an in-place setExtraFilters
   * call would silently no-op.
   *
   * The picker forwards the range via the `native_filters` URL param,
   * rison-encoded with the dashboard's `filter_time` filter id. The
   * legacy ``__time_range`` URL param Superset documents in older guides
   * is a no-op against native filters — verified against Superset 6.0.
   */
  dateFrom?: string;
  dateTo?: string;
  /** Dashboard-native time-filter id. Null disables the external date range. */
  timeFilterId?: string | null;
  /**
   * Sizing for the root wrapper. Defaults to viewport-fill, which is what the
   * standalone partner-view route needs. Callers embedding inside a sized
   * container (eg the admin preview dialog) should pass `tw-h-full tw-w-full`.
   */
  containerClassName?: string;
}

/**
 * Slim Superset embed for the standalone partner view. No date picker, no
 * currency selector, no admin chrome — just the dashboard iframe. Date and
 * partner_id RLS rules are baked into the guest token by the backend, which
 * reads partner_id from the session cookie set by /exchange.
 */
export const PartnerViewEmbed = ({
  supersetUrl,
  dashboardId,
  onEmbedError,
  fetchGuestToken,
  dateFrom,
  dateTo,
  timeFilterId,
  containerClassName = "tw-h-screen tw-w-screen",
}: PartnerViewEmbedProps) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const resolvedTimeFilterId = resolveDashboardTimeFilterId(timeFilterId);

  // Superset SDK is imperative DOM mounting, so this effect is unavoidable.
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    mount.innerHTML = "";

    // Two rAFs so the browser commits layout before the SDK measures the
    // mount's width. Same pattern as SupersetDashboardContainer.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (cancelled) return;
        embedDashboard({
          id: dashboardId,
          supersetDomain: supersetUrl,
          mountPoint: mount,
          fetchGuestToken: async () => {
            if (fetchGuestToken) return fetchGuestToken();
            const config = await getPartnerViewAnalyticsConfig();
            return config.guest_token;
          },
          dashboardUiConfig: {
            // Hide Superset's own dashboard title — the chrome renders a
            // per-partner heading ("{partnerName} — Operator Report" or the
            // admin's per-link label) which is more useful than the shared
            // "Partner Report" name baked into the Superset dashboard.
            hideTitle: true,
            hideTab: false,
            // Show per-chart action menu (⋮) so the partner can download any
            // chart as CSV / Excel directly from Superset, with the same RLS
            // the chart ran under. The chrome's standalone CSV/XLSX buttons
            // hit the backend export endpoint (full report shape);
            // per-chart export covers any specific tile that piques the
            // partner's interest.
            hideChartControls: false,
            // Expose Superset's native filter panel so partners can layer on
            // additional filters (vehicle type, station, etc.) beyond the
            // chrome's date range. The dashboard composition decides which
            // filters appear; the partner can collapse the panel if unused.
            filters: { visible: true, expanded: false },
            // Forward the chrome's date range as a rison-encoded
            // ``native_filters`` URL param. Superset's embedded route reads
            // it once on iframe boot and preselects the dashboard's
            // ``NATIVE_FILTER-time-range`` filter, which scopes every chart
            // via the dashboard's filter_time mechanism (extraFormData.
            // time_range overrides each chart's own time_range at query
            // time). The dependency list below includes dateFrom/dateTo so
            // changing either remounts the iframe with the new param.
            urlParams: {
              ...(resolvedTimeFilterId && dateFrom && dateTo
                ? {
                    native_filters: buildTimeRangeNativeFilter({
                      filterId: resolvedTimeFilterId,
                      from: dateFrom,
                      to: dateTo,
                    }),
                  }
                : {}),
            },
          },
        })
          .then(() => {
            if (cancelled) return;
            setIsLoading(false);
            const iframe = mount.querySelector("iframe");
            if (iframe) {
              iframe.style.width = "100%";
              iframe.style.height = "100%";
              iframe.style.border = "none";
              iframe.style.colorScheme = "light";
            }
          })
          .catch(() => {
            if (cancelled) return;
            setIsLoading(false);
            onEmbedError();
          });
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [
    supersetUrl,
    dashboardId,
    onEmbedError,
    fetchGuestToken,
    dateFrom,
    dateTo,
    resolvedTimeFilterId,
  ]);

  return (
    <div className={cn(containerClassName, "tw-overflow-hidden tw-relative")}>
      {isLoading && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-background">
          <Loading />
        </div>
      )}
      <div
        ref={mountRef}
        className="tw-h-full tw-w-full"
        style={{ colorScheme: "light" }}
      />
    </div>
  );
};
